"use client";

import { DeleteAccountPanel } from "@/components/account/DeleteAccountPanel";
import { CheckCircle2, Mail } from "lucide-react";
import Link from "next/link";

export function DeleteAccountPageClient({ deleted }: { deleted: boolean }) {
  if (deleted) {
    return (
      <div className="space-y-5">
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-300">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <div className="space-y-2">
          <h1 className="text-3xl font-extrabold text-white">Account deleted</h1>
          <p className="max-w-2xl text-base leading-7 text-white/65">
            Your ChickyTutor account has been deleted. Processor deletion jobs may take additional
            time to finish. Any store subscription remains managed separately by Google Play or the
            App Store.
          </p>
        </div>
        <Link
          href="/"
          className="inline-flex text-sm font-bold text-orange-300 hover:text-orange-200"
        >
          Return to ChickyTutor
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <p className="text-xs font-bold uppercase text-orange-300">Privacy control</p>
        <h1 className="text-3xl font-extrabold text-white sm:text-4xl">
          Delete your ChickyTutor account
        </h1>
        <p className="max-w-2xl text-base leading-7 text-white/65">
          Use this page to permanently delete your account and associated learning data from a web
          browser.
        </p>
      </header>

      <section className="rounded-lg border border-white/10 bg-[#121318] p-5 sm:p-6">
        <DeleteAccountPanel />
      </section>

      <section className="border-t border-white/10 pt-6">
        <h2 className="text-base font-bold text-white">What this request removes</h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-white/60">
          <li>Your ChickyTutor account and profile.</li>
          <li>
            Learning history, conversations, progress, saved practice data, and uploaded media.
          </li>
          <li>Personalized memories and linked customer, messaging, and analytics records.</li>
        </ul>
        <p className="mt-3 text-sm leading-6 text-white/60">
          Limited transaction, fraud-prevention, security, tax, accounting, or dispute records may
          be retained when legally required. Store subscriptions are managed separately.
        </p>
      </section>

      <section className="border-t border-white/10 pt-6">
        <div className="flex items-start gap-3">
          <Mail className="mt-1 h-5 w-5 shrink-0 text-orange-300" />
          <div>
            <h2 className="text-base font-bold text-white">Cannot access your account?</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-white/60">
              Email{" "}
              <a
                href="mailto:feedback@chickytutor.com?subject=ChickyTutor%20account%20deletion%20request"
                className="font-semibold text-orange-300 hover:text-orange-200"
              >
                feedback@chickytutor.com
              </a>{" "}
              from the address associated with your account. Include "account deletion request" in
              the subject. We may ask you to verify ownership before processing the request.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
